import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BarChart3, Menu, X } from "lucide-react";
import { useState } from "react";
import { clearToken, isAdminSession } from "@/lib/adminAuth";

export const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isAdmin = isAdminSession();

  // Reports and Campaigns are admin dashboards; visitors only see the public pages.
  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/lookup", label: "Analyze Business" },
    { href: "/reports", label: "Reports", admin: true },
    { href: "/campaigns", label: "Campaigns", admin: true },
    { href: "/pricing", label: "Pricing" }
  ].filter((link) => isAdmin || !link.admin);

  const signOut = () => {
    clearToken();
    setMobileMenuOpen(false);
    navigate("/");
  };

  const isActive = (href) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo">
            <img 
              src="/selletrust-logo.jpg" 
              alt="Selletrust" 
              className="h-9 w-auto object-contain"
              onError={(e) => {
                // Fallback to icon if logo fails to load
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="w-9 h-9 bg-blue-600 rounded-lg items-center justify-center hidden">
              <BarChart3 className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900 font-['Outfit']">Selletrust</span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link key={link.href} to={link.href}>
                <Button
                  variant="ghost"
                  className={`text-sm font-medium transition-colors ${
                    isActive(link.href)
                      ? "text-slate-900 bg-slate-100"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  }`}
                  data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </Button>
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-3">
            {isAdmin && (
              <Button variant="ghost" className="text-sm text-slate-600" onClick={signOut} data-testid="nav-sign-out">
                Sign out
              </Button>
            )}
            <Link to="/lookup">
              <Button 
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-5"
                data-testid="nav-cta"
              >
                Get Started
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-slate-600"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-100">
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link 
                  key={link.href} 
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Button
                    variant="ghost"
                    className={`w-full justify-start text-sm font-medium ${
                      isActive(link.href)
                        ? "text-slate-900 bg-slate-100"
                        : "text-slate-600"
                    }`}
                  >
                    {link.label}
                  </Button>
                </Link>
              ))}
              {isAdmin && (
                <Button variant="ghost" className="w-full justify-start text-sm font-medium text-slate-600" onClick={signOut}>
                  Sign out
                </Button>
              )}
              <Link to="/lookup" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-blue-600 hover:bg-blue-700 mt-2">
                  Get Started
                </Button>
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
